import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Menu, X, Globe, User, LogOut, Headphones, ChevronDown, BarChart3, Shield, Share2, MessageCircle, Facebook, Twitter, Linkedin, Link2, Check, FileText, Star, ListMusic, LayoutDashboard } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { ShareButtons } from './ShareButtons';
import { PWAInstallPrompt } from './PWAInstallPrompt';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Admin emails list
const ADMIN_EMAILS = [
  "tonobrusola@gmail.com",
  "bjempowerment@gmail.com",
  "goto.jesusgarcia.cc@gmail.com"
];

// Share Dropdown Component for Header
const ShareDropdown = ({ language }) => {
  const [copied, setCopied] = useState(false);
  
  const shareUrl = 'https://biosounds.es';
  const shareTitle = language === 'es' 
    ? 'BIOSOUNDS - Neuroacústica aplicada para tu Bienestar' 
    : 'BIOSOUNDS - Applied Neuroacoustics for Your Wellbeing';
  const shareText = language === 'es'
    ? 'Descubre el poder de la música y frecuencias binaurales para mejorar tu bienestar.'
    : 'Discover the power of music and binaural frequencies to improve your wellbeing.';

  const handleShare = (platform) => {
    let url = '';
    
    switch (platform) {
      case 'whatsapp':
        url = `https://wa.me/?text=${encodeURIComponent(`${shareTitle}\n\n${shareText}\n\n${shareUrl}`)}`;
        break;
      case 'twitter':
        url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareTitle)}&url=${encodeURIComponent(shareUrl)}`;
        break;
      case 'facebook':
        url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
        break;
      case 'linkedin':
        url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
        break;
      case 'copy':
        navigator.clipboard.writeText(shareUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        return;
      default:
        return;
    }
    
    window.open(url, '_blank', 'width=600,height=400');
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="share-dropdown-btn"
          className="flex items-center gap-1.5 px-2 sm:px-3 py-1.5 text-sm text-slate-400 hover:text-[#0AFFD6] rounded-lg hover:bg-white/5 transition-colors"
        >
          <Share2 className="w-4 h-4" />
          <span className="hidden 2xl:inline">{language === 'es' ? 'Compartir' : 'Share'}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48 bg-slate-900 border-slate-700">
        <DropdownMenuItem 
          onClick={() => handleShare('whatsapp')}
          className="cursor-pointer hover:bg-slate-800 focus:bg-slate-800"
        >
          <MessageCircle className="w-4 h-4 mr-2 text-[#25D366]" />
          <span>WhatsApp</span>
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={() => handleShare('twitter')}
          className="cursor-pointer hover:bg-slate-800 focus:bg-slate-800"
        >
          <Twitter className="w-4 h-4 mr-2 text-slate-400" />
          <span>Twitter / X</span>
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={() => handleShare('facebook')}
          className="cursor-pointer hover:bg-slate-800 focus:bg-slate-800"
        >
          <Facebook className="w-4 h-4 mr-2 text-[#1877F2]" />
          <span>Facebook</span>
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={() => handleShare('linkedin')}
          className="cursor-pointer hover:bg-slate-800 focus:bg-slate-800"
        >
          <Linkedin className="w-4 h-4 mr-2 text-[#0A66C2]" />
          <span>LinkedIn</span>
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={() => handleShare('copy')}
          className="cursor-pointer hover:bg-slate-800 focus:bg-slate-800"
        >
          {copied ? (
            <>
              <Check className="w-4 h-4 mr-2 text-emerald-400" />
              <span className="text-emerald-400">{language === 'es' ? '¡Copiado!' : 'Copied!'}</span>
            </>
          ) : (
            <>
              <Link2 className="w-4 h-4 mr-2 text-[#0AFFD6]" />
              <span>{language === 'es' ? 'Copiar enlace' : 'Copy link'}</span>
            </>
          )}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [potencialCambio, setPotencialCambio] = useState(null);
  const { language, toggleLanguage, t } = useLanguage();
  const { user, isAuthenticated, logout, hasSubscription, token } = useAuth();
  const location = useLocation();
  
  const isAdmin = user?.is_admin === true || (user?.email && ADMIN_EMAILS.includes(user.email.toLowerCase()));

  // Fetch potencial de cambio for authenticated users
  useEffect(() => {
    const fetchStats = async () => {
      if (!isAuthenticated || !token) return;
      try {
        const response = await fetch(`${API}/sessions/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setPotencialCambio(data.potencial_cambio || 1);
        }
      } catch (error) {
        console.error('Error fetching stats:', error);
      }
    };
    fetchStats();
  }, [isAuthenticated, token]);

  // Public navigation links (only shown when NOT authenticated)
  const publicNavLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/terapias', label: language === 'es' ? 'Terapias' : 'Therapies' },
    {
      path: '/mi-programa',
      label: language === 'es' ? 'Mi programa personalizado' : 'My personalised programme',
      shortLabel: language === 'es' ? 'Mi programa' : 'My programme',
    },
    { path: '/technology', label: t('nav.technology') },
    { path: '/pricing', label: t('nav.pricing') },
    { path: '/blog', label: 'Blog' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 border-b border-white/5 bg-[#030712]/70 backdrop-blur-xl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 xl:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to={isAuthenticated ? "/panel" : "/"} className="flex items-center gap-2 group mr-2 xl:mr-4 flex-shrink-0" data-testid="logo-link">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#0AFFD6] to-[#0B8FFF] flex items-center justify-center">
              <Headphones className="w-4 h-4 text-white" />
            </div>
            <span className="text-xl font-bold font-['Outfit'] tracking-tight gradient-text group-hover:opacity-80 transition-opacity">
              BIOSOUNDS
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {/* Public links - only when NOT authenticated */}
            {!isAuthenticated && publicNavLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.path.replace('/', '') || 'home'}`}
                className={`px-2.5 xl:px-4 py-2 text-sm font-medium rounded-lg whitespace-nowrap transition-colors ${
                  isActive(link.path)
                    ? 'text-[#0AFFD6] bg-[#0AFFD6]/10'
                    : 'text-slate-400 hover:text-[#0AFFD6] hover:bg-white/5'
                }`}
              >
                {link.shortLabel ? (<><span className="hidden 2xl:inline">{link.label}</span><span className="2xl:hidden">{link.shortLabel}</span></>) : link.label}
              </Link>
            ))}
            {/* Private Area Links - Only for authenticated users */}
            {isAuthenticated && (
              <>
                <Link
                  to="/panel"
                  data-testid="nav-mi-panel"
                  className={`px-3 xl:px-4 py-2 text-sm font-medium rounded-lg whitespace-nowrap transition-colors ${
                    isActive('/panel')
                      ? 'text-emerald-300 bg-emerald-500/20 border border-emerald-500/50'
                      : 'text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 hover:bg-emerald-500/20 hover:border-emerald-500/50'
                  }`}
                >
                  <span className="flex items-center gap-1.5">
                    <LayoutDashboard className="w-4 h-4" />
                    {language === 'es' ? 'Mi panel' : 'My panel'}
                  </span>
                </Link>
                <Link
                  to="/dashboard"
                  data-testid="nav-mis-audios"
                  className={`px-3 xl:px-4 py-2 text-sm font-medium rounded-lg whitespace-nowrap transition-colors ${
                    isActive('/dashboard')
                      ? 'text-emerald-300 bg-emerald-500/20 border border-emerald-500/50'
                      : 'text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 hover:bg-emerald-500/20 hover:border-emerald-500/50'
                  }`}
                >
                  <span className="flex items-center gap-1.5">
                    <Headphones className="w-4 h-4" />
                    {language === 'es' ? 'Mis Audios' : 'My Audios'}
                  </span>
                </Link>
                <Link
                  to="/stats"
                  data-testid="nav-mi-progreso"
                  className={`px-3 xl:px-4 py-2 text-sm font-medium rounded-lg whitespace-nowrap transition-colors ${
                    isActive('/stats')
                      ? 'text-emerald-300 bg-emerald-500/20 border border-emerald-500/50'
                      : 'text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 hover:bg-emerald-500/20 hover:border-emerald-500/50'
                  }`}
                >
                  <span className="flex items-center gap-1.5">
                    <BarChart3 className="w-4 h-4" />
                    {language === 'es' ? 'Mi Progreso' : 'My Progress'}
                  </span>
                </Link>
                <Link
                  to="/playlists"
                  data-testid="nav-playlists"
                  className={`px-3 xl:px-4 py-2 text-sm font-medium rounded-lg whitespace-nowrap transition-colors ${
                    isActive('/playlists') || location.pathname.startsWith('/playlists/')
                      ? 'text-emerald-300 bg-emerald-500/20 border border-emerald-500/50'
                      : 'text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 hover:bg-emerald-500/20 hover:border-emerald-500/50'
                  }`}
                >
                  <span className="flex items-center gap-1.5">
                    <ListMusic className="w-4 h-4" />
                    {language === 'es' ? 'Mis Playlists' : 'My Playlists'}
                  </span>
                </Link>
                <Link
                  to="/blog"
                  data-testid="nav-blog"
                  className={`px-3 xl:px-4 py-2 text-sm font-medium rounded-lg whitespace-nowrap transition-colors ${
                    isActive('/blog') || location.pathname.startsWith('/blog/')
                      ? 'text-emerald-300 bg-emerald-500/20 border border-emerald-500/50'
                      : 'text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 hover:bg-emerald-500/20 hover:border-emerald-500/50'
                  }`}
                >
                  <span className="flex items-center gap-1.5">
                    <FileText className="w-4 h-4" />
                    Blog
                  </span>
                </Link>
              </>
            )}
          </nav>

          {/* Right Side */}
          <div className="flex items-center gap-1 sm:gap-3">
            {/* Potencial de Cambio Badge - Only for authenticated users */}
            {isAuthenticated && potencialCambio !== null && (
              <Link
                to="/stats"
                className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-[#0AFFD6]/20 to-[#0B8FFF]/20 border border-[#0AFFD6]/30 hover:border-[#0AFFD6]/50 transition-all group"
                data-testid="potencial-cambio-badge"
                title={language === 'es' ? 'Mi Potencial de Cambio' : 'My Change Potential'}
              >
                <Star className="w-4 h-4 text-[#0AFFD6] group-hover:text-[#0AFFD6]" />
                <span className="text-sm font-bold bg-gradient-to-r from-[#0AFFD6] to-[#0B8FFF] bg-clip-text text-transparent">
                  {potencialCambio}/10
                </span>
              </Link>
            )}

            {/* Share Button */}
            <ShareDropdown language={language} />
            
            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              data-testid="language-toggle"
              className="flex items-center gap-1.5 px-2 sm:px-3 py-1.5 text-sm text-slate-400 hover:text-[#0AFFD6] rounded-lg hover:bg-white/5 transition-colors"
            >
              <Globe className="w-4 h-4" />
              <span className="uppercase font-medium">{language}</span>
            </button>

            {/* Auth Buttons */}
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    data-testid="user-menu-btn"
                    className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 text-slate-300 hover:text-[#0AFFD6]"
                  >
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#0AFFD6]/20 to-[#0B8FFF]/20 flex items-center justify-center border border-white/10">
                      {user?.picture ? (
                        <img src={user.picture} alt="" className="w-full h-full rounded-full" />
                      ) : (
                        <User className="w-4 h-4" />
                      )}
                    </div>
                    <span className="hidden xl:inline max-w-[90px] truncate">{user?.name?.split(' ')[0]}</span>
                    <ChevronDown className="w-4 h-4 hidden sm:block" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-slate-900 border-white/10">
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard" className="flex items-center gap-2" data-testid="dashboard-link">
                      <Headphones className="w-4 h-4" />
                      {t('nav.dashboard')}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/stats" className="flex items-center gap-2" data-testid="stats-link">
                      <BarChart3 className="w-4 h-4" />
                      {language === 'es' ? 'Mi Progreso' : 'My Progress'}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/profile" className="flex items-center gap-2" data-testid="profile-link">
                      <User className="w-4 h-4" />
                      {t('nav.profile')}
                    </Link>
                  </DropdownMenuItem>
                  {isAdmin && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin" className="flex items-center gap-2 text-amber-400" data-testid="admin-link">
                        <Shield className="w-4 h-4" />
                        {language === 'es' ? 'Administración' : 'Admin Panel'}
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    onClick={logout}
                    data-testid="logout-btn"
                    className="flex items-center gap-2 text-red-400 focus:text-red-400"
                  >
                    <LogOut className="w-4 h-4" />
                    {t('nav.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="hidden sm:flex items-center gap-2">
                <Link to="/login" data-testid="login-btn">
                  <Button variant="ghost" className="text-slate-300 hover:text-[#0AFFD6]">
                    {t('nav.login')}
                  </Button>
                </Link>
                <Link to="/register" data-testid="register-btn">
                  <Button className="bg-[#0AFFD6]/10 text-[#0AFFD6] border border-[#0AFFD6]/50 hover:bg-[#0AFFD6]/20 hover:shadow-[0_0_20px_rgba(34,211,238,0.3)]">
                    {t('nav.register')}
                  </Button>
                </Link>
              </div>
            )}

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
              className="lg:hidden p-2 text-slate-400 hover:text-white"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="lg:hidden border-t border-white/5 bg-[#030712]/95 backdrop-blur-xl"
        >
          <div className="px-4 py-4 space-y-2">
            {/* Public links - only when NOT authenticated */}
            {!isAuthenticated && publicNavLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-4 py-3 rounded-lg text-sm font-medium ${
                  isActive(link.path)
                    ? 'text-[#0AFFD6] bg-[#0AFFD6]/10'
                    : 'text-slate-400 hover:text-white hover:bg-white/5'
                }`}
              >
                {link.label}
              </Link>
            ))}
            {/* Private Area Links for Mobile */}
            {isAuthenticated && (
              <>
                <Link
                  to="/panel"
                  data-testid="mobile-nav-mi-panel"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium ${
                    isActive('/panel')
                      ? 'text-emerald-300 bg-emerald-500/20 border border-emerald-500/50'
                      : 'text-emerald-400 bg-emerald-500/10 border border-emerald-500/30'
                  }`}
                >
                  <LayoutDashboard className="w-4 h-4" />
                  {language === 'es' ? 'Mi panel' : 'My panel'}
                </Link>
                <Link
                  to="/dashboard"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium ${
                    isActive('/dashboard')
                      ? 'text-emerald-300 bg-emerald-500/20 border border-emerald-500/50'
                      : 'text-emerald-400 bg-emerald-500/10 border border-emerald-500/30'
                  }`}
                >
                  <Headphones className="w-4 h-4" />
                  {language === 'es' ? 'Mis Audios' : 'My Audios'}
                </Link>
                <Link
                  to="/stats"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium ${
                    isActive('/stats')
                      ? 'text-emerald-300 bg-emerald-500/20 border border-emerald-500/50'
                      : 'text-emerald-400 bg-emerald-500/10 border border-emerald-500/30'
                  }`}
                >
                  <BarChart3 className="w-4 h-4" />
                  {language === 'es' ? 'Mi Progreso' : 'My Progress'}
                </Link>
                <Link
                  to="/playlists"
                  data-testid="mobile-nav-playlists"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium ${
                    location.pathname.startsWith('/playlists')
                      ? 'text-emerald-300 bg-emerald-500/20 border border-emerald-500/50'
                      : 'text-emerald-400 bg-emerald-500/10 border border-emerald-500/30'
                  }`}
                >
                  <ListMusic className="w-4 h-4" />
                  {language === 'es' ? 'Mis Playlists' : 'My Playlists'}
                </Link>

                <Link
                  to="/blog"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium ${
                    isActive('/blog') || location.pathname.startsWith('/blog/')
                      ? 'text-emerald-300 bg-emerald-500/20 border border-emerald-500/50'
                      : 'text-emerald-400 bg-emerald-500/10 border border-emerald-500/30'
                  }`}
                >
                  <FileText className="w-4 h-4" />
                  Blog
                </Link>
                <Link
                  to="/profile"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium ${
                    isActive('/profile')
                      ? 'text-emerald-300 bg-emerald-500/20 border border-emerald-500/50'
                      : 'text-emerald-400 bg-emerald-500/10 border border-emerald-500/30'
                  }`}
                >
                  <User className="w-4 h-4" />
                  {language === 'es' ? 'Mi Perfil' : 'My Profile'}
                </Link>
                {isAdmin && (
                  <Link
                    to="/admin"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium ${
                      isActive('/admin')
                        ? 'text-violet-300 bg-[#0B8FFF]/20 border border-[#0B8FFF]/50'
                        : 'text-[#0B8FFF] bg-[#0B8FFF]/10 border border-[#0B8FFF]/30'
                    }`}
                  >
                    <Shield className="w-4 h-4" />
                    Admin
                  </Link>
                )}
                <button
                  onClick={() => { logout(); setMobileMenuOpen(false); }}
                  className="flex items-center gap-2 w-full px-4 py-3 rounded-lg text-sm font-medium text-red-400 hover:bg-red-500/10"
                >
                  <LogOut className="w-4 h-4" />
                  {language === 'es' ? 'Cerrar Sesión' : 'Logout'}
                </button>
              </>
            )}
            {!isAuthenticated && (
              <div className="pt-4 space-y-2 border-t border-white/10">
                <Link
                  to="/login"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-4 py-3 text-center rounded-lg text-slate-300 hover:bg-white/5"
                >
                  {t('nav.login')}
                </Link>
                <Link
                  to="/register"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-4 py-3 text-center rounded-lg bg-[#0AFFD6]/10 text-[#0AFFD6] border border-[#0AFFD6]/50"
                >
                  {t('nav.register')}
                </Link>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </header>
  );
};

export const Footer = () => {
  const { t, language } = useLanguage();

  return (
    <footer className="border-t border-white/5 bg-[#030712]/50 backdrop-blur-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="col-span-1 md:col-span-2">
            <Link to="/" className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#0AFFD6] to-[#0B8FFF] flex items-center justify-center">
                <Headphones className="w-4 h-4 text-white" />
              </div>
              <span className="text-xl font-bold font-['Outfit'] gradient-text">BIOSOUNDS</span>
            </Link>
            <p className="text-slate-500 text-sm max-w-md mb-4">
              {language === 'es' 
                ? 'Neurobiomodulación sonora avanzada respaldada por más de 50 estudios científicos. Mejora tu sueño, reduce la ansiedad y optimiza tu bienestar.'
                : 'Advanced sound neurobiomodulation backed by over 50 scientific studies. Improve your sleep, reduce anxiety and optimize your wellbeing.'
              }
            </p>
            {/* Support Email */}
            <div className="flex items-center gap-2 text-sm">
              <span className="text-slate-500">{language === 'es' ? 'Soporte:' : 'Support:'}</span>
              <a 
                href="mailto:hola@biosounds.es" 
                className="text-[#0AFFD6] hover:text-[#0AFFD6] transition-colors"
                data-testid="support-email"
              >
                hola@biosounds.es
              </a>
            </div>
          </div>

          {/* Links */}
          <div>
            <h4 className="text-white font-medium mb-4">{language === 'es' ? 'Explora' : 'Explore'}</h4>
            <ul className="space-y-2">
              <li><Link to="/" className="text-slate-500 hover:text-[#0AFFD6] text-sm" data-testid="footer-link-home">{t('nav.home')}</Link></li>
              <li><Link to="/terapias" className="text-slate-500 hover:text-[#0AFFD6] text-sm" data-testid="footer-link-terapias">{language === 'es' ? 'Terapias' : 'Therapies'}</Link></li>
              <li><Link to="/mi-programa" className="text-slate-500 hover:text-[#0AFFD6] text-sm" data-testid="footer-link-mi-programa">{language === 'es' ? 'Mi programa personalizado' : 'My personalised programme'}</Link></li>
              <li><Link to="/technology" className="text-slate-500 hover:text-[#0AFFD6] text-sm" data-testid="footer-link-technology">{t('nav.technology')}</Link></li>
              <li><Link to="/pricing" className="text-slate-500 hover:text-[#0AFFD6] text-sm" data-testid="footer-link-pricing">{t('nav.pricing')}</Link></li>
              <li><Link to="/blog" className="text-slate-500 hover:text-[#0AFFD6] text-sm" data-testid="footer-link-blog">Blog</Link></li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="text-white font-medium mb-4">Legal</h4>
            <ul className="space-y-2">
              <li><Link to="/terms" className="text-slate-500 hover:text-[#0AFFD6] text-sm" data-testid="terms-link">{language === 'es' ? 'Términos de Servicio' : 'Terms of Service'}</Link></li>
              <li><Link to="/privacy" className="text-slate-500 hover:text-[#0AFFD6] text-sm" data-testid="privacy-link">{language === 'es' ? 'Política de Privacidad' : 'Privacy Policy'}</Link></li>
              <li><Link to="/cookies" className="text-slate-500 hover:text-[#0AFFD6] text-sm" data-testid="cookies-link">{language === 'es' ? 'Política de Cookies' : 'Cookie Policy'}</Link></li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-white/5 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-slate-600 text-sm">
            © {new Date().getFullYear()} BIOSOUNDS. {language === 'es' ? 'Todos los derechos reservados.' : 'All rights reserved.'}
          </p>
          
          {/* Social Share Buttons */}
          <ShareButtons variant="compact" />
          
          <p className="text-slate-600 text-xs hidden sm:block">
            {language === 'es' 
              ? 'Los resultados pueden variar. Consulte a un profesional de la salud antes de comenzar cualquier terapia.'
              : 'Results may vary. Consult a healthcare professional before starting any therapy.'
            }
          </p>
        </div>
      </div>
    </footer>
  );
};
